'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export interface ThemePreset {
  id: string;
  name: string;
  accent: string;
  accentLight: string;
  accentMid: string;
  accentBorder: string;
}

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'rose', name: 'Rose', accent: '#E8A0B8', accentLight: '#FDD9E5', accentMid: '#F9F0F4', accentBorder: '#F0D9E4' },
  { id: 'violet', name: 'Violet', accent: '#9B7FD4', accentLight: '#EDE5FF', accentMid: '#F5F0FF', accentBorder: '#DDD0F5' },
  { id: 'sky', name: 'Sky', accent: '#5BA8D4', accentLight: '#D9EEFF', accentMid: '#EEF6FF', accentBorder: '#C0DDEF' },
  { id: 'mint', name: 'Mint', accent: '#5DC4A0', accentLight: '#D5F5EB', accentMid: '#EDFAF5', accentBorder: '#B8E8D8' },
  { id: 'amber', name: 'Amber', accent: '#D4A24A', accentLight: '#FFF0CC', accentMid: '#FFF8E8', accentBorder: '#F0DCA0' },
  { id: 'coral', name: 'Coral', accent: '#E07060', accentLight: '#FFE0DA', accentMid: '#FFF0EE', accentBorder: '#F5C8C0' },
  { id: 'slate', name: 'Slate', accent: '#6B8CAE', accentLight: '#D8E8F5', accentMid: '#EEF4FA', accentBorder: '#C0D4E8' },
  { id: 'peach', name: 'Peach', accent: '#E09060', accentLight: '#FFE8D5', accentMid: '#FFF4EE', accentBorder: '#F5D0B0' },
];

export interface AppTheme {
  presetId: string;
  preset: ThemePreset;
}

interface ThemeContextValue {
  theme: AppTheme;
  setThemePreset: (presetId: string) => void;
}

const defaultTheme: AppTheme = {
  presetId: 'slate',
  preset: THEME_PRESETS.find(p => p.id === 'slate') || THEME_PRESETS[0],
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  setThemePreset: () => {},
});

export const useTheme = () => useContext(ThemeContext);

function applyThemeToDom(preset: ThemePreset) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--primary', preset.accentLight);
  root.style.setProperty('--accent', preset.accent);
  root.style.setProperty('--secondary', preset.accentMid);
  root.style.setProperty('--border', preset.accentBorder);
  root.style.setProperty('--input', preset.accentMid);
  root.style.setProperty('--ring', preset.accentLight);
  root.style.setProperty('--muted', preset.accentMid);
  root.style.setProperty('--muted-foreground', preset.accent);
}

/**
 * Single shared key for the active theme — written by staff, read by customers.
 * This ensures customers always see the same theme the staff has selected.
 */
const SHARED_THEME_KEY = 'vq_theme_active';

function resolveTheme(): AppTheme {
  if (typeof window === 'undefined') return defaultTheme;

  // Read from the single shared key — works for both staff and customers
  const stored = localStorage.getItem(SHARED_THEME_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const preset = THEME_PRESETS.find(p => p.id === parsed.presetId) || THEME_PRESETS[0];
      return { presetId: preset.id, preset };
    } catch {}
  }

  // Fallback: check legacy per-staff key for backward compatibility
  const staffId = localStorage.getItem('vq_staff_id') || 'default';
  const legacyStored = localStorage.getItem(`vq_theme_${staffId}`);
  if (legacyStored) {
    try {
      const parsed = JSON.parse(legacyStored);
      const preset = THEME_PRESETS.find(p => p.id === parsed.presetId) || THEME_PRESETS[0];
      return { presetId: preset.id, preset };
    } catch {}
  }

  return defaultTheme;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<AppTheme>(defaultTheme);

  useEffect(() => {
    // Load theme on mount
    const loaded = resolveTheme();
    setTheme(loaded);
    applyThemeToDom(loaded.preset);

    // Listen for theme changes from other pages (e.g. staff changes theme in settings)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === SHARED_THEME_KEY || (e.key && e.key.startsWith('vq_theme_'))) {
        const updated = resolveTheme();
        setTheme(updated);
        applyThemeToDom(updated.preset);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setThemePreset = (presetId: string) => {
    const preset = THEME_PRESETS.find(p => p.id === presetId) || THEME_PRESETS[0];
    const next: AppTheme = { presetId: preset.id, preset };
    setTheme(next);
    applyThemeToDom(preset);

    // Write to the shared key so ALL users (staff + customers) pick it up
    localStorage.setItem(SHARED_THEME_KEY, JSON.stringify({ presetId: preset.id }));

    // Also keep the legacy per-staff key for backward compat
    const staffId = localStorage.getItem('vq_staff_id') || 'default';
    localStorage.setItem(`vq_theme_${staffId}`, JSON.stringify({ presetId: preset.id }));
  };

  return (
    <ThemeContext.Provider value={{ theme, setThemePreset }}>
      {children}
    </ThemeContext.Provider>
  );
}
